<template lang="pug">
LoadingArea(:loading="props.loading")
  FullCalendar(ref="fullCalendarRef" :options="calendarOptions")
  //- pre {{ events }}
  Teleport(to="body")
    Transition(name="tooltip")
      .text-canvas-100.rounded-lg.event-tooltip.pt-4.pb-4.px-5(
        v-if="tooltipVisible" 
        :style="tooltipStyle" 
        class="bg-canvas-800/89 dark:bg-canvas-800/95 border border-canvas-900"
      )
        //- pre hoveredEvent: {{ hoveredEvent }}
        .flex.flex-col.gap-3(class="min-w-[180px]")
          UserAvatar(:user="hoveredEvent.owner" showFullName size="small")
          .my-1.bg-canvas-300(style="height: 1px;")
          .flex.items-center.gap-2.ml-1
            span.inline-block.rounded-full.shrink-0(
              class="w-[13px] h-[13px]"
              :class="appColors.eventCss(hoveredEvent)?.bgDark"
            )
            span.text-lg.font-semibold {{ hoveredEvent.eventableType === 'Tour' ? hoveredEvent.tour?.transferee?.fullName : hoveredEvent.name }}
          //- .text-sm {{ hoveredEvent.category?.label }}
          Tag(:value="hoveredEvent.category?.label" :class="[appColors.eventCss(hoveredEvent)?.bgDark, appColors.eventCss(hoveredEvent)?.textLight]")

          .my-1.bg-canvas-300(style="height: 1px;")
          span.text-center.font-semibold.text-nowrap {{ format.dateRangeWithTime(hoveredEvent.startAt, hoveredEvent.endAt) }}
          span.text-center(v-if="!hoveredEvent.allDay") {{ format.humanizeDuration(hoveredEvent.startAt, hoveredEvent.endAt) }}
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import { appColors } from '@/lib'
import { format } from '@/utils'
import { useResponsive } from '@/use'

const props = defineProps({
  events:        { type: Array,   default: () => [] },
  loading:       { type: Boolean, default: false },
  calendarOwner: { type: Object,  default: () => ({}) },
  initialDate:   { type: String,  default: null },
})

const emit = defineEmits(['eventClick', 'datesSet'])

const fullCalendarRef = ref(null)
const { isMobile, isTouchDevice } = useResponsive()

defineExpose({
  prev:  () => fullCalendarRef.value.getApi().prev(),
  next:  () => fullCalendarRef.value.getApi().next(),
  today: () => fullCalendarRef.value.getApi().today(),
})

watch(isMobile, (mobile) => {
  fullCalendarRef.value?.getApi().changeView(mobile ? 'listMonth' : 'dayGridMonth')
})

const tooltipVisible = ref(false)
const tooltipStyle   = ref({})
const hoveredEvent   = ref(null)

function getEventTitle(evt) {
  // Non-tour events just use the default event name
  if (evt.eventableType !== 'Tour') {
    return evt.name
  } else {
  // Tour events will show either the Destination Guide name or Transferee name 
  // This means that when a Guide views their own calendar the events will show the Transferee linked to the tour
  // However Admins that see all guide's events will see the name of the Guide (instead of Transferee)
    const guide = evt.owner
    if (props.calendarOwner?.id !== guide?.id) {
      return guide?.fullName
    } else {
      return evt.tour?.transferee?.fullName
    }
  }
}

const calendarEvents = computed(() => {
  return props.events.map((event) => {
    const eventTitle = getEventTitle(event)

    // FullCalendar uses exclusive end dates for all-day events. Our DB stores end_at as
    // 23:59:00 on the last day (e.g. "2026-05-14T23:59:00-07:00"). We extract the local
    // date and advance it by 1 day using UTC arithmetic to avoid timezone drift.
    let endWithAllDayFix = event.endAt
    if (event.allDay && endWithAllDayFix) {
      const [year, month, day] = endWithAllDayFix.substring(0, 10).split('-').map(Number)
      endWithAllDayFix = new Date(Date.UTC(year, month - 1, day + 1)).toISOString().split('T')[0]
    }

    return {
      id:              `event-${event.id}`,
      title:           eventTitle,
      start:           event.startAt,
      end:             endWithAllDayFix,
      allDay:          event.allDay,
      backgroundColor: appColors.eventCss(event)?.rgbColor,
      borderColor:     appColors.eventCss(event)?.rgbColor,
      extendedProps:   { event },
    }
  })
})

function onEventMouseEnter({ event: calendarEvent, el }) {
  if (isTouchDevice.value) return // don't show the tooltip on touch devices
  const rect = el.getBoundingClientRect()
  hoveredEvent.value = calendarEvent.extendedProps.event
  tooltipStyle.value = {
    position:  'fixed',
    left:      `${rect.left + rect.width / 2}px`,
    top:       `${rect.top - 8}px`,
    transform: 'translateX(-50%) translateY(-100%)',
    zIndex:    9999,
  }
  tooltipVisible.value = true
}

function onEventMouseLeave() {
  tooltipVisible.value = false
}

const calendarOptions = computed(() => ({
  plugins:      [dayGridPlugin, listPlugin],
  initialView:  isMobile.value ? 'listMonth' : 'dayGridMonth',
  headerToolbar: false,
  events:       calendarEvents.value,
  height:       'auto',
  ...(props.initialDate ? { initialDate: props.initialDate } : {}),
  datesSet:     (arg) => emit('datesSet', arg),
  eventClick:   ({ event: calendarEvent }) => {
    const apiEvent = calendarEvent.extendedProps.event
    if (apiEvent) emit('eventClick', apiEvent)
  },
  eventMouseEnter: onEventMouseEnter,
  eventMouseLeave: onEventMouseLeave,
}))
</script>

<style scoped>
:deep(.fc-event) {
  cursor: pointer;
}

.event-tooltip {
  pointer-events: none;
}
</style>
